import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Table, Dropdown, Button } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { eventActionTypeOptions } from '../../../../../helpers/constants';
import { getTranslatedMonth } from '../../../../../helpers/localization';
import { formatCurrency, isPrimaryBankAccount } from './helper';
import { ActionCell } from './StrategyBalanceSheet';
import useLocalization from '../../../../../hooks/useLocalization';
import { Calendar, DollarSign, X, SlidersHorizontal, Search } from 'lucide-react';

const getOneOffEventHeaders = translate => [
  { key: 'description', label: translate('description') },
  { key: 'value', label: translate('value'), isCurrency: true },
  { key: 'action', label: translate('actions') },
  { key: 'name', label: translate('name') },
  { key: 'year', label: translate('year') },
  { key: 'month', label: translate('month') }
];

const getNameOfInstrument = (event, assetsAndLiabilities, translate) => {
  const instrument = assetsAndLiabilities.find(el => el._id === event.refId);
  if (!instrument) return '-';

  return isPrimaryBankAccount(instrument) ? translate('primary_bank_account') : instrument.name;
};

const matchesEventSearch = (event, query) =>
  (event.description || '').toLowerCase().includes(query) || (event.name || '').toLowerCase().includes(query);

const EventFiltersUI = ({ filtersMetaData, setFiltersMetaData }) => {
  const { translate, isRTL } = useLocalization();
  const filterPanelRef = useRef(null);
  const toggleButtonRef = useRef(null);

  const [draftFilters, setDraftFilters] = useState(filtersMetaData.data);

  const [placement, setPlacement] = useState('bottom');
  const PANEL_HEIGHT = 220;

  // Sync draft and calculate placement when panel opens
  useEffect(() => {
    if (filtersMetaData.open) {
      setDraftFilters(filtersMetaData.data);

      if (toggleButtonRef.current) {
        const rect = toggleButtonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < PANEL_HEIGHT && spaceAbove > spaceBelow) {
          setPlacement('top');
        } else {
          setPlacement('bottom');
        }
      }
    }
  }, [filtersMetaData.open, filtersMetaData.data]);

  // Handle outside click
  useEffect(() => {
    if (!filtersMetaData.open) return;
    const handler = e => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) {
        setFiltersMetaData(prev => ({ ...prev, open: false }));
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filtersMetaData.open, setFiltersMetaData]);

  const handleApply = () => {
    let count = 0;
    if (draftFilters.value.valueFrom !== '' || draftFilters.value.valueTo !== '') count++;
    if (draftFilters.date.startDate || draftFilters.date.endDate) count++;

    setFiltersMetaData(prev => ({
      ...prev,
      data: draftFilters,
      open: false,
      isFilterApplied: count > 0,
      noOfFiltersApplied: count
    }));
  };

  const handleClear = () => {
    setFiltersMetaData(prev => ({
      ...prev,
      data: {
        date: { startDate: null, endDate: null },
        value: { valueFrom: '', valueTo: '' }
      },
      open: false,
      isFilterApplied: false,
      noOfFiltersApplied: 0
    }));
  };

  const popoverStyle = {
    position: 'absolute',
    backgroundColor: '#fff',
    border: '1px solid #dee2e6',
    borderRadius: 10,
    boxShadow: '0 8px 30px rgba(0,0,0,0.14)',
    width: 320,
    maxHeight: PANEL_HEIGHT,
    zIndex: 1100,
    overflow: 'hidden',
    ...(isRTL ? { right: -50, left: 'auto' } : { left: -50, right: 'auto' }),
    ...(placement === 'top' ? { bottom: 'calc(100% + 10px)' } : { top: 'calc(100% + 10px)' })
  };

  return (
    <div style={{ position: 'relative' }} ref={filterPanelRef}>
      <button
        ref={toggleButtonRef}
        type="button"
        className="btn btn-sm rounded-lg border border-black bg-white text-sm py-1.5 d-flex align-items-center gap-2 shadow-sm m-0"
        onClick={() => setFiltersMetaData(prev => ({ ...prev, open: !prev.open }))}
      >
        <SlidersHorizontal size={14} />
        {translate('filters')}
        {filtersMetaData.noOfFiltersApplied > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 16,
              height: 16,
              borderRadius: '50%',
              backgroundColor: '#0c1b33',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1,
              marginLeft: 2
            }}
          >
            {filtersMetaData.noOfFiltersApplied}
          </span>
        )}
      </button>

      {filtersMetaData.open && (
        <div style={popoverStyle}>
          {/* Panel header */}
          <div
            className="d-flex align-items-center justify-content-between px-3 py-2"
            style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}
          >
            <div className="d-flex align-items-center gap-2">
              <SlidersHorizontal size={14} />
              <span className="fw-semibold text-dark">{translate('filters')}</span>
            </div>
            <button
              type="button"
              className="btn btn-sm p-0 d-flex align-items-center text-primary-dark"
              style={{ lineHeight: 1 }}
              onClick={() => setFiltersMetaData(prev => ({ ...prev, open: false }))}
            >
              <X size={14} />
            </button>
          </div>

          {/* Panel body */}
          <div className="px-3 py-2">
            {/* Value range */}
            <div className="mb-3 flex align-items-center gap-2">
              <span
                className="text-sm fw-semibold text-dark d-block"
                style={{ width: '55px' }}
              >
                {translate('value')}
              </span>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="number"
                  className="form-control form-control-sm border-secondary-subtle"
                  placeholder={translate('from')}
                  value={draftFilters.value.valueFrom}
                  min={0}
                  onChange={e =>
                    setDraftFilters(prev => ({ ...prev, value: { ...prev.value, valueFrom: e.target.value } }))
                  }
                  style={{ flex: 1, minWidth: 0 }}
                />
                <span className="text-muted text-sm">–</span>
                <input
                  type="number"
                  className="form-control form-control-sm border-secondary-subtle"
                  placeholder={translate('to')}
                  value={draftFilters.value.valueTo}
                  min={0}
                  onChange={e =>
                    setDraftFilters(prev => ({ ...prev, value: { ...prev.value, valueTo: e.target.value } }))
                  }
                  style={{ flex: 1, minWidth: 0 }}
                />
              </div>
            </div>

            {/* Date range */}
            <div className="mb-3 flex align-items-center gap-2">
              <span
                className="text-sm fw-semibold text-dark d-block"
                style={{ width: '55px' }}
              >
                {translate('date')}
              </span>
              <div style={{ width: '100%' }}>
                <DatePicker
                  selectsRange
                  startDate={draftFilters.date.startDate}
                  endDate={draftFilters.date.endDate}
                  onChange={([start, end]) =>
                    setDraftFilters(prev => ({ ...prev, date: { startDate: start, endDate: end } }))
                  }
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  wrapperClassName="w-100"
                  className="form-control form-control-sm w-100 border-secondary-subtle"
                  placeholderText={translate('select_range') || 'Select range…'}
                  isClearable
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="d-flex gap-2" style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12, paddingBottom: 4 }}>
              {filtersMetaData.isFilterApplied && (
                <button
                  type="button"
                  className="btn btn-sm text-sm m-0"
                  style={{ flex: 1, borderColor: '#f8d7da', color: '#dc3545' }}
                  onClick={handleClear}
                >
                  {translate('clear')}
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-dark text-sm fw-semibold m-0"
                style={{ flex: 1 }}
                onClick={handleApply}
              >
                {translate('apply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LabeledFilterDropdown = ({ label, value, options, onChange, menuStyle }) => {
  const selected = options.find(o => o.value === value);

  return (
    <Dropdown>
      <Dropdown.Toggle
        variant="white"
        className="d-flex justify-content-between align-items-center border border-black bg-white shadow-sm m-0"
      >
        <span className="text-sm">
          <span className="text-sm fw-semibold text-dark">{label}:</span> {selected?.label}
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="shadow" style={menuStyle}>
        {options.map(opt => (
          <Dropdown.Item key={opt.value || 'all'} active={opt.value === value} onClick={() => onChange(opt.value)}>
            {opt.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const EventNameSearch = ({ value, onChange, placeholder, isRTL }) => (
  <div className="position-relative" style={{ width: 220 }}>
    <Search
      size={14}
      className="position-absolute text-muted"
      style={{
        top: '50%',
        transform: 'translateY(-50%)',
        ...(isRTL ? { right: 10 } : { left: 10 }),
        pointerEvents: 'none'
      }}
    />
    <input
      type="text"
      autoComplete="off"
      className="form-control form-control-sm border-black shadow-sm m-0"
      style={{
        borderRadius: 8,
        paddingLeft: isRTL ? 8 : 28,
        paddingRight: isRTL ? 28 : 8
      }}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  </div>
);

const OneOffEventSheet = ({
  strategy,
  onEditEventClick,
  onOneOffEventDeleteClick,
  onAddEventClick,
  editable,
  filterByInstrument = null,
}) => {
  const { translate, langCode, isRTL } = useLocalization();
  const [sortOption, setSortOption] = useState('date_desc');
  const [eventNameQuery, setEventNameQuery] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState('');
  const [linkedInstrumentFilter, setLinkedInstrumentFilter] = useState('');
  const [filtersMetaData, setFiltersMetaData] = useState({
    data: {
      date: { startDate: null, endDate: null },
      value: { valueFrom: '', valueTo: '' }
    },
    open: false,
    noOfFiltersApplied: 0,
    isFilterApplied: false
  });
  const showTitle = filterByInstrument ? false : true;
  const showAddLinkedEventButton = Boolean(filterByInstrument && onAddEventClick && editable);
  const showTabToolbar = !filterByInstrument && (strategy.oneOffChanges || []).length > 0;

  const oneOffEventHeaders = useMemo(() => {
    const headers = getOneOffEventHeaders(translate);
    return filterByInstrument ? headers.filter(h => h.key !== 'name') : headers;
  }, [langCode, translate, filterByInstrument]);

  const oneOffEvents = useMemo(() => {
    let rows = strategy.oneOffChanges || [];
    if (filterByInstrument?.refId != null && filterByInstrument?.type) {
      rows = rows.filter(e => e.refId === filterByInstrument.refId && e.type === filterByInstrument.type);
    }

    const assetsAndLiabilities = [...(strategy.assets || []), ...(strategy.liabilities || [])];

    if (!filterByInstrument) {
      if (actionTypeFilter) {
        rows = rows.filter(e => e.action === actionTypeFilter);
      }
      if (linkedInstrumentFilter) {
        rows = rows.filter(e => e.refId === linkedInstrumentFilter);
      }
    }

    // apply filters if filters meta data is applied
    if (filtersMetaData.isFilterApplied) {
      const { value, date } = filtersMetaData.data;

      // Range filtering
      rows = rows.filter(e => {
        const val = Number(e.value);
        const from = value.valueFrom !== '' ? Number(value.valueFrom) : -Infinity;
        const to = value.valueTo !== '' ? Number(value.valueTo) : Infinity;
        return val >= from && val <= to;
      });

      // Date filtering
      if (date.startDate || date.endDate) {
        rows = rows.filter(e => {
          const eventYear = Number(e.year);
          const eventMonth = Number(e.month);

          let afterStart = true;
          if (date.startDate) {
            const startYear = date.startDate.getFullYear();
            const startMonth = date.startDate.getMonth();
            afterStart = eventYear > startYear || (eventYear === startYear && eventMonth >= startMonth);
          }

          let beforeEnd = true;
          if (date.endDate) {
            const endYear = date.endDate.getFullYear();
            const endMonth = date.endDate.getMonth();
            beforeEnd = eventYear < endYear || (eventYear === endYear && endMonth <= endMonth);
          }

          return afterStart && beforeEnd;
        });
      }
    }

    let result = rows.map(event => ({
      ...event,
      name: getNameOfInstrument(event, assetsAndLiabilities, translate),
      attribute: assetsAndLiabilities.find(el => el._id === event.refId)
    }));

    const nameQuery = eventNameQuery.trim().toLowerCase();
    if (!filterByInstrument && nameQuery) {
      result = result.filter(event => matchesEventSearch(event, nameQuery));
    }

    if (sortOption === 'value_desc') {
      result.sort((a, b) => Number(b.value) - Number(a.value));
    } else if (sortOption === 'value_asc') {
      result.sort((a, b) => Number(a.value) - Number(b.value));
    } else if (sortOption === 'date_desc') {
      result.sort((a, b) => {
        if (a.year !== b.year) return Number(b.year) - Number(a.year);
        return Number(b.month) - Number(a.month);
      });
    } else if (sortOption === 'date_asc') {
      result.sort((a, b) => {
        if (a.year !== b.year) return Number(a.year) - Number(b.year);
        return Number(a.month) - Number(b.month);
      });
    }
    return result;
  }, [
    strategy,
    filterByInstrument,
    translate,
    langCode,
    sortOption,
    filtersMetaData,
    eventNameQuery,
    actionTypeFilter,
    linkedInstrumentFilter
  ]);

  const actionTypeOptions = useMemo(
    () => [
      { value: '', label: translate('all') },
      ...eventActionTypeOptions.map(action => ({ value: action, label: action }))
    ],
    [translate]
  );

  const linkedInstrumentOptions = useMemo(() => {
    const assetsAndLiabilities = [...(strategy.assets || []), ...(strategy.liabilities || [])];
    const seen = new Set();
    const options = [];

    (strategy.oneOffChanges || []).forEach(event => {
      if (!event?.refId || seen.has(event.refId)) return;
      seen.add(event.refId);
      options.push({
        value: event.refId,
        label: getNameOfInstrument(event, assetsAndLiabilities, translate)
      });
    });

    options.sort((a, b) => a.label.localeCompare(b.label, isRTL ? 'he' : 'en'));
    return [{ value: '', label: translate('all') }, ...options];
  }, [strategy, translate, isRTL]);

  const sortOptionsList = useMemo(
    () => [
      {
        label: (
          <div className="d-flex align-items-center gap-2 text-sm">
            <Calendar size={14} />
            {translate('date')} {translate('sort_desc')}
          </div>
        ),
        value: 'date_desc'
      },
      {
        label: (
          <div className="d-flex align-items-center gap-2 text-sm">
            <Calendar size={14} />
            {translate('date')} {translate('sort_asc')}
          </div>
        ),
        value: 'date_asc'
      },
      {
        label: (
          <div className="d-flex align-items-center gap-2 text-sm">
            <DollarSign size={14} />
            {translate('value')} {translate('sort_desc')}
          </div>
        ),
        value: 'value_desc'
      },
      {
        label: (
          <div className="d-flex align-items-center gap-2 text-sm">
            <DollarSign size={14} />
            {translate('value')} {translate('sort_asc')}
          </div>
        ),
        value: 'value_asc'
      }
    ],
    [translate]
  );

  return (
    <div id="oneOffEventSection">
      <div
        className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap"
        style={{ position: 'relative', zIndex: 1050  }}
      >
        {showTitle && (
          <h6 className="m-0">
            <b>{translate('one_time_events')}</b>
          </h6>
        )}
        {showTabToolbar && (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <EventNameSearch
              value={eventNameQuery}
              onChange={setEventNameQuery}
              placeholder={translate('search_by_event_name')}
              isRTL={isRTL}
            />
            <LabeledFilterDropdown
              label={translate('action_type')}
              value={actionTypeFilter}
              options={actionTypeOptions}
              onChange={setActionTypeFilter}
            />
            <LabeledFilterDropdown
              label={translate('asset_or_liability')}
              value={linkedInstrumentFilter}
              options={linkedInstrumentOptions}
              onChange={setLinkedInstrumentFilter}
              menuStyle={{ maxHeight: 240, overflowY: 'auto' }}
            />
            <EventFiltersUI filtersMetaData={filtersMetaData} setFiltersMetaData={setFiltersMetaData} />
            <div className="d-flex gap-2 align-items-center" style={{ width: '180px' }}>
            <Dropdown className="w-100">
              <Dropdown.Toggle
                variant="white"
                className="w-100 d-flex justify-content-between align-items-center border border-black bg-white shadow-sm m-0"
              >
                <span className="text-sm flex gap-2"><span className="text-sm fw-semibold text-dark">{translate('sort_by')}:</span> {sortOptionsList.find(o => o.value === sortOption)?.label}</span>
              </Dropdown.Toggle>

              <Dropdown.Menu className="w-100 shadow">
                {sortOptionsList.map(opt => (
                  <Dropdown.Item
                    key={opt.value}
                    active={opt.value === sortOption}
                    onClick={() => setSortOption(opt.value)}
                  >
                    {opt.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        )}
      </div>
      <div className="table-responsive rounded-lg">
        <Table className="text-sm m-0">
          <thead>
            <tr>
              {oneOffEventHeaders.map(h => (
                <th key={h.key} style={{ minWidth: 100 }} className="bg-table-header text-white">
                  {h.label}
                </th>
              ))}
              <th style={{ minWidth: 100 }} className="bg-table-header text-white">
                {translate('actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {oneOffEvents?.length > 0 ? (
              oneOffEvents.map(l => (
                <tr className="bg-white" key={l._id}>
                  {oneOffEventHeaders.map(h =>
                    h.key === 'month' ? (
                      <td style={{ minWidth: 100 }} key={h.key}>
                        {getTranslatedMonth(translate, l['month'], { short: true })}
                      </td>
                    ) : (
                      <td style={{ minWidth: 100 }} key={h.key}>
                        {Array.isArray(l[h.key])
                          ? l[h.key].join(', ')
                          : h.isCurrency
                          ? formatCurrency(l[h.key])
                          : l[h.key]}
                      </td>
                    )
                  )}
                  <td>
                    <ActionCell
                      hideAddEvent={true}
                      disabled={!editable}
                      onEditClick={() => {
                        onEditEventClick(l);
                      }}
                      onDeleteClick={() => onOneOffEventDeleteClick(l)}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr className="bg-white text-center shadow-lg border border-muted">
                <td colSpan={oneOffEventHeaders.length + 1}>
                  <h6 className="m-0">{translate('nothing_to_show')}</h6>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {showAddLinkedEventButton && (
        <div className="mt-3">
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="text-white rounded-pill px-2 py-1"
            onClick={onAddEventClick}
          >
            {translate('new_one_time_event')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default OneOffEventSheet;
